import './IdeasAndAdvice.scss'

import React, { useRef } from 'react'

import { Button, Card, Carousel, CarouselSlide, Column, FluidImage, GridContainer, H2, H3, Hidden, Icon, ProgressiveImage, Row, Visible } from '@vp/swan'

import { READ_MORE_CTA_DETAIL, IDEAS_AND_ADVICE__DATA_SECTION_LABEL } from '../WixWebsitesAnalytics'
import MissingContentAlert from '../MissingContentAlert'
import { McsFeatureGroupModel, McsHeroModel } from '../types/McsTypes'
import { AccessibilityContent } from '../types/AccessibilityTypes'

interface IdeasAndAdviceProps {
  content: McsHeroModel
  articleOne: Function
  articleTwo: Function
  articleThree: Function
  articleFour: Function
  accessibility: AccessibilityContent
}

const IdeasAndAdvice = ({ content, articleOne, articleTwo, articleThree, articleFour, accessibility }: IdeasAndAdviceProps) => {
  const buttonHandlers = useRef([() => articleOne(), () => articleTwo(), () => articleThree(), () => articleFour()])

  if (!content) {
    return <MissingContentAlert />
  }

  return (
    <GridContainer>
      <Row>
        <Column span={12}>
          <H2 m={0} fontSkin="title-section" fontSize="x2large">
            {content.title}
          </H2>
        </Column>
      </Row>

      <Visible xs>
        <Row py={6}>
          <Column span={12}>
            <Carousel
              initialSlide={0}
              slidesToShow={1.15}
              arrows={false}
              dots
              slidesToScroll={1}
              gridGutters
              pb={{ xs: 5, sm: 5, md: 5 }}
              accessibleTextForDots={
                content?.heroMetadata
                  ? content?.heroMetadata.map((feature, index) => accessibility.features[5]?.name.replace('{{SLIDE_NUMBER}}', String(index + 1)) ?? '')
                  : []
              }
              accessibleTextForVisibleSlides={accessibility.features[2]?.valueText}
              accessibleTextForSlide={accessibility.features[2]?.altText}
              accessibleTextForNext={accessibility.features[4].name}
              accessibleTextForPrevious={accessibility.features[3].name}
              aria-roledescription={accessibility.features[0].name}
              aria-label={content.title}
            >
              {content?.heroMetadata?.map((feature, index: number) => (
                <CarouselSlide as={Card} className="wix-websites-pdp__slide" key={feature.title} aria-roledescription={accessibility.features[1].name}>
                  <Feature feature={feature as McsFeatureGroupModel} handleOnClick={buttonHandlers.current[index]} />
                </CarouselSlide>
              ))}
            </Carousel>
          </Column>
        </Row>
      </Visible>

      <Hidden xs>
        <Row mt={6}>
          {content?.heroMetadata?.map((feature, index: number) => (
            <Column key={feature.title} span={3}>
              <Card className="wix-websites-pdp__ideas-and-advice__feature-container" evenHeight>
                <Feature feature={feature as McsFeatureGroupModel} handleOnClick={buttonHandlers.current[index]} />
              </Card>
            </Column>
          ))}
        </Row>
      </Hidden>
    </GridContainer>
  )
}

interface FeatureProps {
  feature: McsFeatureGroupModel
  handleOnClick: Function
}

const Feature = ({ feature, handleOnClick }: FeatureProps) => {
  return (
    <>
      <ProgressiveImage
        placeholder={feature.mediaLink?.[0]?.secure_url || ''}
        src={feature.mediaLink?.[0]?.secure_url || ''}
        srcSet={feature.mediaLink?.[0]?.secure_url || ''}
        sizes="(max-width: 768px) 100vw, (max-width: 2160px) 25vw, 540px"
      >
        {(image) => <FluidImage alt={feature.description} src={image.src} srcSet={image.srcSet} sizes={image.sizes} />}
      </ProgressiveImage>

      <H3 mt={5} mb={0} fontSize="large">
        {feature.title}
      </H3>

      <Button
        mt={6}
        onClick={() => handleOnClick()}
        data-section={IDEAS_AND_ADVICE__DATA_SECTION_LABEL}
        data-position="11"
        data-translation={READ_MORE_CTA_DETAIL}
      >
        {feature?.features?.[0]?.name}

        <Icon iconType="arrowRight" />
      </Button>
    </>
  )
}

export default IdeasAndAdvice
